import type { MilkdownPlugin } from '@milkdown/kit/ctx';
import type { Node as ProseMirrorNode } from '@milkdown/kit/prose/model';
import { $node, $remark } from '@milkdown/kit/utils';
// unist-util-visit is used to traverse the markdown AST (mdast) to find and transform nodes.
// This is the standard approach for remark plugins that need to modify the AST.
// We use it to find paragraph nodes containing standalone embed links and replace them
// with our custom embedNode type before Milkdown converts the AST to ProseMirror nodes.
// See: https://unifiedjs.com/explore/package/unist-util-visit/
import type { Link, Paragraph, Root, Text } from 'mdast';
import { visit } from 'unist-util-visit';

import * as inputRules from './input-rules';
// Import from providers barrel to ensure all providers register themselves
import { embedRegistry } from './providers';

const detectEmbedType = (url: string) => embedRegistry.detectType(url);

/**
 * Parse dimensions from text (e.g., "https://... 640x360" -> { width: 640, height: 360 })
 */
function parseDimensionsFromText(text: string): { width: number | null; height: number | null } {
  const parts = text.trim().split(' ');
  const lastPart = parts[parts.length - 1];

  if (lastPart && /^\d+x\d+$/.test(lastPart)) {
    const [width, height] = lastPart.split('x').map(Number);

    return { width, height };
  }

  return { width: null, height: null };
}

/**
 * mdast-util-directive nodes (leaf or text) expose `name` and `attributes`.
 * We only need the subset used for `::iframe{src=…}`.
 */
interface IframeDirectiveMdast {
  name: string;
  attributes?: Record<string, string | null | undefined> | null;
}

function parseDirectiveSize(value: string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = parseInt(String(value), 10);

  return Number.isFinite(n) ? n : null;
}

/** `src` is required; width/height come from directive attributes when present. */
function readEmbedDirectivePayload(node: IframeDirectiveMdast): {
  src: string;
  width: number | null;
  height: number | null;
  embedType: string;
} | null {
  // Typed embeds serialize as ::loom{src=...} / ::iframe{src=...}; name must be a simple identifier.
  if (!node.name || !/^[a-zA-Z]+$/.test(node.name)) return null;

  const src = node.attributes?.src;

  if (!src) return null;

  return {
    src,
    width: parseDirectiveSize(node.attributes?.width),
    height: parseDirectiveSize(node.attributes?.height),
    embedType: node.name,
  };
}

/** iframe-only directive payload (inline textDirective / escaped ::iframe paths). */
function readIframeDirectivePayload(node: IframeDirectiveMdast): {
  src: string;
  width: number | null;
  height: number | null;
} | null {
  const payload = readEmbedDirectivePayload(node);
  if (!payload || payload.embedType !== 'iframe') return null;

  return { src: payload.src, width: payload.width, height: payload.height };
}

function mdastEmbedNodeFromDirective(payload: { src: string; width: number | null; height: number | null; embedType: string }) {
  return {
    type: 'embedNode' as const,
    attributes: {
      href: payload.src,
      embedType: payload.embedType,
      width: payload.width,
      height: payload.height,
    },
  };
}

function mdastEmbedNodeFromIframe(payload: { src: string; width: number | null; height: number | null }) {
  return mdastEmbedNodeFromDirective({ ...payload, embedType: 'iframe' });
}

function mdastStandaloneUrlLink(url: string) {
  return {
    type: 'link' as const,
    url,
    children: [{ type: 'text' as const, value: url }],
  };
}

/**
 * Whether the paragraph has no meaningful content besides `soleChild` (whitespace text and breaks only).
 * Used when a block `::iframe` was parsed as an inline textDirective inside one paragraph.
 */
function paragraphHasOnlyChildPlusTrivialWhitespace(paragraph: Paragraph, soleChild: Paragraph['children'][number]): boolean {
  for (const child of paragraph.children) {
    if (child === soleChild) continue;
    if (child.type === 'text') {
      if (!/^\s*$/.test((child as Text).value)) return false;

      continue;
    }
    if (child.type === 'break') continue;

    return false;
  }

  return true;
}

export const EMBED_NODE_NAME = 'embed';

/**
 * Remark plugin that transforms:
 * 1. Paragraphs containing only a standalone link (where text === href) matching embed patterns
 * 2. Leaf directives like ::iframe{src="..."}
 * 3. Text directives :iframe / escaped ::iframe in phrasing context → embedNode or link
 *
 * into embedNode AST nodes (or a plain link when the directive is truly inline).
 */
export const remarkEmbedPlugin = $remark('embed-transform', () => {
  return () => (tree: Root) => {
    // Handle standalone links in paragraphs (e.g., YouTube URLs)
    visit(tree, 'paragraph', (node: Paragraph, index, parent) => {
      if (index === undefined || !parent) return;

      // Check if paragraph has exactly one child and it's a link
      if (node.children.length !== 1 || node.children[0].type !== 'link') return;

      const link = node.children[0] as Link;
      const href = link.url;

      // Check if it's a standalone link (text content equals href)
      const textContent = link.children
        .filter((child): child is Text => child.type === 'text')
        .map((child) => child.value)
        .join('');

      // Parse dimensions first (e.g., "https://... 640x360" -> { width: 640, height: 360 })
      // This must happen BEFORE the standalone check since serialized links may include dimensions
      const { width, height } = parseDimensionsFromText(textContent);

      // Check if it's a standalone link:
      // - Text equals href (no dimensions), OR
      // - Text matches "href WxH" pattern (with dimensions from toMarkdown serialization)
      const textWithoutDimensions = width !== null && height !== null ? textContent.replace(/ \d+x\d+$/, '') : textContent;
      const isStandaloneLink = textWithoutDimensions === href;
      if (!isStandaloneLink) return;

      // Check if URL matches any embed pattern
      const embedType = detectEmbedType(href);
      if (!embedType) return;

      // Replace the entire paragraph with embed node
      (parent.children as any)[index] = {
        type: 'embedNode',
        attributes: { href, embedType, width, height },
      };
    });

    // Block `::loom{…}` / `::iframe{…}` → leafDirective → embedNode
    visit(tree, 'leafDirective', (node: IframeDirectiveMdast, index, parent) => {
      if (index === undefined || !parent) return;

      const payload = readEmbedDirectivePayload(node);

      if (!payload) return;

      (parent.children as any)[index] = mdastEmbedNodeFromDirective(payload);
    });

    // Same spelling sometimes parses as textDirective (list indent, leading `\`, etc.). Milkdown has no
    // handler for raw textDirective → normalize before ParserState runs.
    visit(tree, 'paragraph', (node: Paragraph, index, parent) => {
      if (index === undefined || !parent) return;

      const iframeTextDirectives = node.children.filter((c) => c.type === 'textDirective' && (c as IframeDirectiveMdast).name === 'iframe');

      if (iframeTextDirectives.length !== 1) return;

      const iframeNode = iframeTextDirectives[0];
      const payload = readIframeDirectivePayload(iframeNode as IframeDirectiveMdast);

      if (!payload) return;

      if (!paragraphHasOnlyChildPlusTrivialWhitespace(node, iframeNode)) return;

      (parent.children as any)[index] = mdastEmbedNodeFromIframe(payload);
    });

    // Iframe still inline next to other text → keep URL as a normal link instead of crashing the parser.
    visit(tree, 'textDirective', (node, index, parent) => {
      if (index === undefined || !parent) return;

      const payload = readIframeDirectivePayload(node as IframeDirectiveMdast);

      if (!payload) return;

      (parent.children as any)[index] = mdastStandaloneUrlLink(payload.src);
    });
  };
});

/**
 * Embed node schema for ProseMirror
 * Similar to iframe plugin pattern from Milkdown docs
 */
export const embedNode = $node(EMBED_NODE_NAME, () => ({
  group: 'block',
  atom: true,
  isolating: true,
  draggable: true,
  marks: '',
  attrs: {
    src: { default: null },
    href: { default: null }, // Legacy: ydoc/collab stores URL as 'href'
    embedType: { default: null },
    width: { default: null },
    height: { default: null },
  },
  parseDOM: [
    {
      tag: 'div.embed-block',
      getAttrs: (dom) => {
        const element = dom as HTMLElement;
        const url = element.getAttribute('data-src') || element.getAttribute('data-href');

        return {
          src: url,
          href: url,
          embedType: element.getAttribute('data-embed-type'),
          width: parseInt(element.getAttribute('data-width') ?? '', 10) || null,
          height: parseInt(element.getAttribute('data-height') ?? '', 10) || null,
        };
      },
    },
  ],
  toDOM: (node: ProseMirrorNode) => {
    const url = node.attrs.src || node.attrs.href;

    return [
      'div',
      {
        class: 'embed-block',
        'data-src': url,
        'data-href': url, // Also output href for ydoc compat
        'data-embed-type': node.attrs.embedType,
        'data-width': node.attrs.width,
        'data-height': node.attrs.height,
        contenteditable: 'false',
      },
    ];
  },
  parseMarkdown: {
    match: (node) => node.type === 'embedNode',
    runner: (state, node, type) => {
      const attrs = node.attributes as {
        href: string;
        embedType: string;
        width?: number | null;
        height?: number | null;
      };
      state.addNode(type, {
        href: attrs.href,
        embedType: attrs.embedType,
        width: attrs.width ?? null,
        height: attrs.height ?? null,
      });
    },
  },
  toMarkdown: {
    match: (node) => node.type.name === EMBED_NODE_NAME,
    runner: (state, node) => {
      const url = (node.attrs.href || node.attrs.src) as string;
      const embedType = node.attrs.embedType as string;
      const width = node.attrs.width as number | null;
      const height = node.attrs.height as number | null;
      const hasDimensions = width && height;

      // Only custom iframes need leafDirective (::iframe{src=...}). Help-center / markdown
      // viewers embed Loom/YouTube/etc. from standalone links, matching Platform's default
      // serializer (useEmbedDirective off). Never emit a root-level mdast `text` node.
      if (embedType === 'iframe') {
        const attributes: Record<string, string> = { src: url };
        if (hasDimensions) {
          attributes.width = String(Math.round(width));
          attributes.height = String(Math.round(height));
        }
        state.addNode('leafDirective', undefined, undefined, {
          name: embedType,
          attributes,
        });

        return;
      }

      // Provider embeds (loom, youtube, …) and untyped → paragraph + link [url WxH](url)
      const linkText = hasDimensions ? `${url} ${Math.round(width)}x${Math.round(height)}` : url;
      state.openNode('paragraph');
      state.openNode('link', undefined, { url });
      state.addNode('text', undefined, linkText);
      state.closeNode();
      state.closeNode();
    },
  },
}));

/**
 * Export all embed plugins for registration
 */
export const embedPlugin: MilkdownPlugin[] = [
  remarkEmbedPlugin,
  embedNode,
  ...Object.values(inputRules), // import all input rules from input-rules/index.ts
].flat();
